import type { IndicatorDef, IndicatorInstance, IndicatorRun } from "@tt/shared";
import { useCallback, useMemo } from "react";

import { api } from "../lib/api";
import { useAsync, usePersisted } from "./useApi";

/** Applied indicators are per-chart state the user expects to survive a reload. */
const STORAGE_KEY = "tt.indicators";

const DEFAULTS: IndicatorInstance[] = [
  { instance_id: "ema21", indicator_id: "ema", params: { length: 21 } },
  { instance_id: "ema50", indicator_id: "ema", params: { length: 50 } },
  { instance_id: "rsi14", indicator_id: "rsi", params: { length: 14 } },
];

export function useIndicators(symbol: string, timeframe: string, bars: number) {
  const [instances, setInstances] = usePersisted<IndicatorInstance[]>(
    STORAGE_KEY, DEFAULTS,
  );

  const catalogue = useAsync((s) => api.indicatorCatalogue(s), []);

  // Only the configuration matters to the server, so the request key is built
  // from that rather than from array identity — otherwise every render would
  // refetch.
  const key = useMemo(
    () =>
      instances
        .map((i) => `${i.instance_id}:${i.indicator_id}:${JSON.stringify(i.params)}`)
        .join("|"),
    [instances],
  );

  const run = useAsync<IndicatorRun>(
    (s) => api.runIndicators({ symbol, timeframe, bars, instances }, s),
    [symbol, timeframe, bars, key],
    instances.length > 0,
  );

  const byId = useMemo(() => {
    const map = new Map<string, IndicatorDef>();
    for (const d of catalogue.data?.indicators ?? []) map.set(d.id, d);
    return map;
  }, [catalogue.data]);

  const toggleVisible = useCallback(
    (instanceId: string) => {
      setInstances(
        instances.map((i) =>
          i.instance_id === instanceId
            ? { ...i, visible: i.visible === false }
            : i,
        ),
      );
    },
    [instances, setInstances],
  );

  /** Move an oscillator onto price, or an overlay into its own pane. */
  const setPane = useCallback(
    (instanceId: string, pane: "overlay" | "separate" | null) => {
      setInstances(
        instances.map((i) =>
          i.instance_id === instanceId ? { ...i, pane_override: pane } : i,
        ),
      );
    },
    [instances, setInstances],
  );

  const duplicate = useCallback(
    (instanceId: string) => {
      const src = instances.find((i) => i.instance_id === instanceId);
      if (!src) return;
      let n = 1;
      const taken = new Set(instances.map((i) => i.instance_id));
      let id = `${src.indicator_id}_${n}`;
      while (taken.has(id)) id = `${src.indicator_id}_${++n}`;
      const at = instances.findIndex((i) => i.instance_id === instanceId);
      const next = [...instances];
      next.splice(at + 1, 0, { ...src, instance_id: id });
      setInstances(next);
    },
    [instances, setInstances],
  );

  /** Reorder panes; TradingView calls this moving an indicator up or down. */
  const move = useCallback(
    (instanceId: string, delta: number) => {
      const at = instances.findIndex((i) => i.instance_id === instanceId);
      if (at < 0) return;
      const to = Math.max(0, Math.min(instances.length - 1, at + delta));
      if (to === at) return;
      const next = [...instances];
      const [item] = next.splice(at, 1);
      if (item) next.splice(to, 0, item);
      setInstances(next);
    },
    [instances, setInstances],
  );

  const add = useCallback(
    (indicatorId: string, params: Record<string, unknown> = {}) => {
      // Instance ids must be unique and stable; a counter suffix keeps two
      // copies of the same indicator distinguishable in the manifest.
      let n = 1;
      let id = `${indicatorId}_${n}`;
      const taken = new Set(instances.map((i) => i.instance_id));
      while (taken.has(id)) id = `${indicatorId}_${++n}`;
      setInstances([...instances, { instance_id: id, indicator_id: indicatorId, params }]);
    },
    [instances, setInstances],
  );

  const remove = useCallback(
    (instanceId: string) => {
      setInstances(instances.filter((i) => i.instance_id !== instanceId));
    },
    [instances, setInstances],
  );

  const update = useCallback(
    (instanceId: string, params: Record<string, unknown>) => {
      setInstances(
        instances.map((i) =>
          i.instance_id === instanceId ? { ...i, params: { ...i.params, ...params } } : i,
        ),
      );
    },
    [instances, setInstances],
  );

  const clear = useCallback(() => setInstances([]), [setInstances]);

  return {
    instances, catalogue, run, byId,
    add, remove, update, clear, toggleVisible, setPane, duplicate, move,
  };
}
